import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Interface pour les items transférés au réapprovisionnement
export interface ReorderItem {
  productId: string;
  productName: string;
  sku: string;
  vendor: string; // Non optionnel pour garantir sa présence
  inventory: number;
  price: number;
  cost: number;
  recommendedQuantity: number;
  priorityScore: number;
  daysUntilStockout: number;
  priorityLevel: string;
  priorityColor: string;
  dailySales: number;
  projectedProfit: number;
  projectedRevenue: number;
  totalCost: number;
}

// Interface pour un emplacement avec ses données de réapprovisionnement
export interface LocationReorderData {
  locationId: string; // ID de l'emplacement
  sourceLocationName: string; // Nom de l'emplacement
  reorderItems: ReorderItem[]; // Articles à réapprovisionner pour cet emplacement
}

interface ReorderState {
  // Tableau d'objets, un par emplacement
  locationReorderDataArray: LocationReorderData[];
  // Emplacement actuellement sélectionné
  currentLocation: string;
}

const initialState: ReorderState = {
  locationReorderDataArray: [],
  currentLocation: ""
};

const reorderSlice = createSlice({
  name: "reorder",
  initialState,
  reducers: {
    setReorderData: (
      state, 
      action: PayloadAction<{ 
        reorderItems: ReorderItem[]; 
        sourceLocation: string;
        sourceLocationName: string;
      }>
    ) => {
      const { reorderItems, sourceLocation, sourceLocationName } = action.payload;
      
      // S'assurer que tous les items ont un vendor (utiliser chaîne vide si absent)
      const itemsWithVendor = reorderItems.map(item => ({
        ...item,
        vendor: item.vendor || ""
      }));
      
      // Vérifier si un objet pour cet emplacement existe déjà
      const existingLocationIndex = state.locationReorderDataArray.findIndex(
        location => location.locationId === sourceLocation
      );
      
      if (existingLocationIndex !== -1) {
        // Remplacer les données pour l'emplacement existant
        state.locationReorderDataArray[existingLocationIndex] = {
          locationId: sourceLocation,
          sourceLocationName,
          reorderItems: itemsWithVendor
        };
      } else {
        // Ajouter un nouvel emplacement avec ses données
        state.locationReorderDataArray.push({
          locationId: sourceLocation,
          sourceLocationName,
          reorderItems: itemsWithVendor
        });
      }
      
      // Mettre à jour l'emplacement actuel
      state.currentLocation = sourceLocation;
    },
    clearReorderData: (state, action: PayloadAction<string | undefined>) => {
      const locationId = action.payload;
      
      if (locationId) {
        // Supprimer uniquement les données pour l'emplacement spécifié
        state.locationReorderDataArray = state.locationReorderDataArray.filter(
          location => location.locationId !== locationId
        );
        
        // Si l'emplacement courant est supprimé, réinitialiser
        if (state.currentLocation === locationId) {
          state.currentLocation = "";
        }
      } else {
        // Effacer toutes les données
        state.locationReorderDataArray = [];
        state.currentLocation = "";
      }
    },
    setCurrentLocation: (state, action: PayloadAction<string>) => {
      // Définir l'emplacement actuel
      state.currentLocation = action.payload;
    }
  }
});

export const { setReorderData, clearReorderData, setCurrentLocation } = reorderSlice.actions;

export default reorderSlice.reducer;
